<template>
  <div class="d-flex flex-grow-1 cdt-page">
    <CdtTreeView
      :value="showTreeViewModal"
      :selected-cann-group="selectedCannGroup || {}"
      :cdt-ref="treeViewCdt"
      @close="closeCdtTreeViewModal"
    />

    <v-card class="step-tab-panel" flat>
      <v-container class="pa-0 ma-0 cdt-container d-flex flex-column">
        <ScenarioMeasuringHeader
          :header-text="$t('customerDecisionTree.title')"
          :columns="4"
          tooltip-link="toolguide/090-cdt.html"
        />
        <v-row no-gutters class="no-gutters cdt-panel d-flex flex-grow-1">
          <!-- Cann group menu -->
          <v-col :cols="2">
            <CannGroupMenu
              :mark-cann-group-with-selected-cdt="true"
              :preselect-cann-group="true"
              :selected-cann-group="dirtySelectedCannGroup"
              @on-cann-group-selected="selectCannGroup"
            >
              <template #tickbox>
                <v-icon color="#d3d3d3" class="float-right"> mdi-checkbox-blank-circle </v-icon>
              </template>
            </CannGroupMenu>
          </v-col>

          <!-- Main panel -->
          <v-col class="d-flex flex-column" :cols="10">
            <ProgressBar
              v-if="isCdtGenerationJobRunning"
              :message="$t('messages.inProgress')"
              :percentage="progressBarInfo.progress"
              :status="progressBarInfo.status"
              class="progress-bar"
            />

            <div
              v-if="hasCtdGenerated && !isCdtGenerationJobRunning"
              class="d-flex flex-column flex-cdt-container"
            >
              <div class="d-flex flex-grow-1 inner-flex-container">
                <!-- Tool generated -->
                <div class="d-flex">
                  <div
                    v-for="toolGeneratedCdt in toolGeneratedCdts"
                    :key="toolGeneratedCdt._id"
                    class="d-flex"
                  >
                    <CdtAttributesList
                      :disabled="isEditingDisabled"
                      :selected-cann-group="selectedCannGroup"
                      :cdt="toolGeneratedCdt"
                      @generate="openRegenerateCdtModal"
                      @open-tree-view-modal="openTreeViewModal"
                      @data-changed="hasTooldataChanges = $event"
                    />
                  </div>
                </div>

                <!-- User modified -->
                <div class="d-flex flex-cdt-container">
                  <div
                    v-for="userModifiedCdt in userModifiedCdts"
                    :key="userModifiedCdt._id"
                    class="d-flex"
                  >
                    <CdtAttributesList
                      :ref="el => registerUserModifiedCdtRef(userModifiedCdt, el)"
                      :disabled="isEditingDisabled"
                      :selected-cann-group="selectedCannGroup"
                      :cdt="userModifiedCdt"
                      @generate="openRegenerateCdtModal"
                      @open-tree-view-modal="openTreeViewModal"
                      @data-changed="hasUserChanges = $event"
                    />
                  </div>
                </div>

                <!-- Create modified CDT -->
                <v-col
                  class="attributes-column pa-0 d-flex flex-column justify-space-between"
                  style="vertical-align: top"
                >
                  <v-container class="ma-0 pa-0 controls">
                    <v-row
                      no-gutters
                      class="d-flex justify-start align-center heading-row pa-0 ma-0"
                    >
                      <v-col class="pl-3">
                        <v-btn
                          variant="text"
                          :disabled="disableCreateModifiedCdt"
                          class="addModifiedCdt"
                          @click="createModifiedCDT()"
                        >
                          <v-icon class="mr-1 ml-2"> mdi-plus-box </v-icon>
                          {{ tkey('cdtList.createModifiedCdt') }}
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-divider />
                  </v-container>
                  <!-- Empty footer placeholder to match CdtAttributesList footer alignment -->
                  <div class="attributes-actions-container pt-2 pb-0 mt-1 d-flex align-center" />
                </v-col>
              </div>
            </div>

            <!-- Initial generation selection -->
            <CdtAttributeSelection
              v-if="!isCdtGenerationJobRunning && !hasCtdGenerated && selectedCannGroup"
              :disabled="isEditingDisabled"
              :selected-cann-group="selectedCannGroup"
              @generate="startInitialCdtGeneration"
              @data-changed="hasAttributeSelectionChanges = $event"
            />

            <!-- Page actions -->
            <div v-if="!isCdtGenerationJobRunning" class="page-actions-container">
              <PageActions
                :show-export="showNotImplemented"
                :show-discard="false"
                :show-save-button="showNotImplemented"
                :save-disabled="isEditingDisabled"
                :is-discard-enabled="!isEditingDisabled"
                :has-data-changes="false"
                :has-data-errors="hasDataErrors"
                save-btn-text="saveCompleteCdt"
              >
                <template #right-btns>
                  <div class="generate-multiple-btn-container">
                    <SelectMultipleCannGroupsModal
                      :selected-cann-group="selectedCannGroup || {}"
                      :title="tkey('dialog.title')"
                      :subtitle="tkey('dialog.title')"
                      :has-attribute-list-changes="
                        hasTooldataChanges || hasAttributeSelectionChanges
                      "
                      @confirm="startInitialCdtGeneration"
                    >
                      <template #activator="{ props: activatorProps }">
                        <v-btn
                          v-bind="activatorProps"
                          :disabled="isEditingDisabled"
                          data-id-e2e="btnGenerateCDTMultiple"
                          variant="flat"
                          primary
                        >
                          {{ tkey('generateMultiple') }}
                        </v-btn>
                      </template>
                    </SelectMultipleCannGroupsModal>
                  </div>
                </template>
              </PageActions>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- confirmation dialog -->
    <v-dialog
      v-model="isRegenerateCDTModalOpen"
      retain-focus
      max-width="450"
      content-class="rtls-dialog"
      @keydown.esc="closeRegenerateCdtModal"
      @click:outside="closeRegenerateCdtModal"
    >
      <BaseDialogCard
        ref="confirm"
        :title="tkey('confirmation.title')"
        :border="true"
        @close="closeRegenerateCdtModal"
      >
        <v-card-text class="text-h6">
          <p>{{ tkey('confirmation.question') }}</p>
          <strong>{{ tkey('confirmation.warning') }}</strong>
        </v-card-text>

        <template #footer>
          <v-card-actions class="justify-center">
            <v-btn data-id-e2e="btnOKGenerateCDT" primary @click="closeRegenerateCdtModal">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn secondary variant="flat" class="outlined-btn" @click="startCdtGeneration">
              {{ $t('actions.ok') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="onUnsavedDataModalCancel"
      @confirm="confirmUnsavedDataModal"
    />
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';
import { useScenariosStore } from '@/stores/scenarios';
import { useAiStore } from '@/stores/ai';
import { useFeature } from '@/composables/use-feature';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import cannGroupUtils from '@/utils/cann-groups';
import scenarioCdtTypes from '@enums/scenario-cdt-types';
import {
  cloneDeep,
  difference,
  find,
  get,
  has,
  isEqual,
  isUndefined,
  keyBy,
  map,
  pick,
  property,
  some,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },

  setup() {
    const instance = getCurrentInstance();
    const { canEditCDTPage } = usePermission();
    const { aiPanelEnabled } = useFeature();
    const cdtsStore = useScenarioCdtsStore();

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () => {
        const proxy = instance.proxy;
        if (proxy.isEditingDisabled) {
          return [
            'generate-cdt',
            'create-modified-cdt',
            'delete-modified-cdt',
            'reorder-cdt-attributes',
            'remove-cdt-attributes',
            'save-modified-cdt',
            'select-cdt',
          ];
        }
        const excluded = [];
        if (!proxy.hasCtdGenerated) {
          excluded.push(
            'select-cdt',
            'create-modified-cdt',
            'delete-modified-cdt',
            'reorder-cdt-attributes',
            'remove-cdt-attributes',
            'save-modified-cdt'
          );
        } else {
          if (proxy.disableCreateModifiedCdt) excluded.push('create-modified-cdt');
          // Only exclude delete if there are no user-modified CDTs that can actually be deleted
          // (i.e. all existing ones are currently selected — matching the UI's disabled button logic)
          const deletableCdts = proxy.userModifiedCdts?.filter(c => !c.selected) ?? [];
          if (!deletableCdts.length) excluded.push('delete-modified-cdt');
          // Do NOT exclude reorder-cdt-attributes here even when no user-modified CDT exists yet:
          // the user may ask to create one and immediately reorder in the same turn.
          // Only one CDT exists and it is already selected — selecting again is a no-op
          if (proxy.toolGeneratedCdts?.[0]?.selected && !proxy.userModifiedCdts?.length) {
            excluded.push('select-cdt');
          }
        }
        if (proxy.isCdtGenerationJobRunning) excluded.push('generate-cdt');
        return excluded;
      },
      getSupplementary: () => {
        const proxy = instance.proxy;
        const toolSelected = proxy.toolGeneratedCdts?.[0]?.selected;
        const userSelected = proxy.userModifiedCdts?.[0]?.selected;
        let currentlySelected;
        if (toolSelected) currentlySelected = 'tool-generated';
        else if (userSelected) currentlySelected = 'user-modified';
        else currentlySelected = 'none';

        const deletableModifiedCdts = proxy.userModifiedCdts?.filter(c => !c.selected) ?? [];
        const deletableSummary =
          deletableModifiedCdts.length === 0
            ? 'No deletable user-modified CDTs (all are selected or none exist).'
            : deletableModifiedCdts
                .map(
                  (c, i) => `#${i + 1}: id=${c._id ?? 'unsaved'}, selected=${c.selected ?? false}`
                )
                .join('; ');

        const allUserModifiedCdts = proxy.userModifiedCdts ?? [];
        const cdtAttributeSummary =
          allUserModifiedCdts.length === 0
            ? 'No user-modified CDTs.'
            : allUserModifiedCdts
                .map((c, i) => {
                  const attrs = (c.attributes ?? [])
                    .filter(a => a.isSelected !== false)
                    .map(a => {
                      const scenAttr = proxy.scenarioAttributes?.find(s => s.id === a.attributeId);
                      return scenAttr?.name ?? a.attributeId;
                    });
                  return `#${i + 1}: id=${c._id ?? 'unsaved'}, attributes (top to bottom): [${attrs.join(', ')}]`;
                })
                .join(' | ');

        return {
          'select-cdt': `Currently selected CDT: ${currentlySelected}.`,
          'delete-modified-cdt': `Deletable user-modified CDTs for current cann group: ${deletableSummary}`,
          'reorder-cdt-attributes': `User-modified CDTs for current cann group: ${cdtAttributeSummary}`,
          'remove-cdt-attributes': `User-modified CDTs for current cann group: ${cdtAttributeSummary}`,
          'save-modified-cdt': `User-modified CDTs for current cann group: ${cdtAttributeSummary}`,
        };
      },
      actions: {
        'generate-cdt': {
          description:
            'Trigger CDT generation for the currently selected cann group. ' +
            'Only call when the user explicitly asks to generate or re-generate. ' +
            'Switching analysis must already be complete before this can be run. ' +
            'Generation is asynchronous — the result will appear once the job completes; do not wait or poll. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Starting CDT generation',
          confirmationLabel: () =>
            `generate CDT for "${instance.proxy.selectedCannGroup?.name || 'current cann group'}"`,
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';
            if (!proxy.selectedCannGroup)
              return '[Tool] Action failed: no cann group is selected. Ask the user to select one first.';
            if (proxy.isCdtGenerationJobRunning)
              return '[Tool] Action failed: CDT generation is already running for this cann group. Ask the user to wait for it to finish.';
            await proxy.startInitialCdtGeneration({ cannGroupIds: [proxy.selectedCannGroup.key] });
            return `[Tool] Action succeeded: CDT generation job started for "${proxy.selectedCannGroup.name}". The result will appear once the job completes.`;
          },
        },
        'create-modified-cdt': {
          description:
            'Create a user-modified copy of the tool-generated CDT for the current cann group. ' +
            'Call this when the user asks to create a user-modified (or custom) CDT, or asks to set a custom attribute priority/order. ' +
            'If the user also specifies a desired attribute order in the same request, call reorder-cdt-attributes immediately after this succeeds — do not wait for a follow-up message. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Creating modified CDT',
          confirmationLabel: () =>
            `create a user-modified CDT for "${instance.proxy.selectedCannGroup?.name || 'current cann group'}"`,
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';
            if (!proxy.hasCtdGenerated)
              return '[Tool] Action failed: no tool-generated CDT exists yet. The CDT must be generated first.';
            if (proxy.disableCreateModifiedCdt)
              return '[Tool] Action failed: a user-modified CDT already exists or there are unsaved changes to the tool-generated CDT. The user must save or discard those changes first.';
            proxy.createModifiedCDT();
            const hasUnsavedModified = proxy.userModifiedCdts?.some(c => !c._id);
            if (!hasUnsavedModified)
              return '[Tool] Action failed: the modified CDT was not created as expected. Ask the user to try manually.';
            return '[Tool] Action succeeded: user-modified CDT created. The user can now reorder the attributes or drag and drop them directly in the list to change their priority (top = most important).';
          },
        },
        'delete-modified-cdt': {
          description:
            'Delete a single user-modified CDT for the current cann group. ' +
            'Only call when the user explicitly asks to delete a specific CDT. ' +
            'A CDT that is currently selected CANNOT be deleted — this mirrors the disabled delete button in the UI. ' +
            'If more than one deletable user-modified CDT exists, you MUST ask the user which one they want deleted ' +
            'and re-call with the cdtId parameter — do NOT delete them all. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Deleting modified CDT',
          confirmationLabel: args => {
            const cannGroupName = instance.proxy.selectedCannGroup?.name || 'current cann group';
            const cdtId = args?.cdtId;
            return cdtId
              ? `delete user-modified CDT (id: ${cdtId}) for "${cannGroupName}"`
              : `delete the user-modified CDT for "${cannGroupName}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              cdtId: {
                type: 'string',
                description:
                  'The _id of the specific user-modified CDT to delete. ' +
                  'Required when more than one deletable user-modified CDT exists for the current cann group. ' +
                  'Obtain the available IDs from the supplementary context for this tool.',
              },
            },
            required: [],
          },
          handler: async ({ cdtId } = {}) => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';

            const deletableCdts = proxy.userModifiedCdts?.filter(c => !c.selected) ?? [];

            if (!deletableCdts.length) {
              const hasSelectedModified = proxy.userModifiedCdts?.some(c => c.selected);
              if (hasSelectedModified)
                return '[Tool] Action failed: the user-modified CDT is currently selected as the active CDT and cannot be deleted. Inform the user that they must first switch the active CDT to the tool-generated one before deleting.';
              return '[Tool] Action failed: no user-modified CDT found for this cann group.';
            }

            let cdt;
            if (cdtId) {
              cdt = deletableCdts.find(c => c._id === cdtId);
              if (!cdt) {
                const isSelectedCdt = proxy.userModifiedCdts?.some(
                  c => c._id === cdtId && c.selected
                );
                if (isSelectedCdt)
                  return '[Tool] Action failed: the specified user-modified CDT is currently selected and cannot be deleted. Inform the user they must first switch to the tool-generated CDT.';
                return '[Tool] Action failed: no non-selected user-modified CDT with that id was found. Ask the user to confirm which CDT to delete.';
              }
            } else if (deletableCdts.length === 1) {
              cdt = deletableCdts[0];
            } else {
              const idList = deletableCdts
                .map((c, i) => `#${i + 1}: id=${c._id ?? 'unsaved'}`)
                .join(', ');
              return `[Tool] Action failed: multiple deletable user-modified CDTs exist (${idList}). Ask the user which specific CDT they want to delete, then re-call with the cdtId parameter.`;
            }

            if (!cdt._id) {
              cdtsStore.deleteUnsavedModifiedCdt(cdt);
            } else {
              await cdtsStore.deleteModifiedCdt({ cdtId: cdt._id });
            }

            const targetId = cdt._id;
            const stillExists = targetId
              ? proxy.userModifiedCdts?.some(c => c._id === targetId)
              : proxy.userModifiedCdts?.some(c => !c._id);
            if (stillExists)
              return '[Tool] Action failed: the modified CDT could not be deleted. Ask the user to try manually.';
            return '[Tool] Action succeeded: user-modified CDT deleted.';
          },
        },
        'reorder-cdt-attributes': {
          description:
            'Reorder the attributes in a user-modified CDT for the current cann group. ' +
            'Works on both saved and unsaved (newly created, not yet saved) CDTs — saving is NOT required first. ' +
            'IMPORTANT: the supplementary attribute list below is evaluated at message-send time. ' +
            'If a CDT was just created earlier in this same turn, it will not appear in the list below but IS available — call this tool and the handler will find it. ' +
            'The order in the CDT determines attribute priority from top (highest) to bottom (lowest). ' +
            'Accepts a full or partial ordered list of attribute names. ' +
            'If the list is partial, the specified attributes are moved to the positions given and all remaining ' +
            'attributes keep their existing relative order, appended after the specified ones. ' +
            'Use the supplementary context to find current attribute order and CDT IDs. ' +
            'If the user refers to "the first one", "the last one", or describes attributes inside a CDT, ' +
            'resolve the target CDT from the supplementary context before calling. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Reordering CDT attributes',
          confirmationLabel: args => {
            const cannGroupName = instance.proxy.selectedCannGroup?.name || 'current cann group';
            const cdtId = args?.cdtId;
            const preview = (args?.attributeOrder ?? []).slice(0, 3).join(', ');
            const suffix = (args?.attributeOrder ?? []).length > 3 ? ', …' : '';
            return cdtId
              ? `reorder attributes in CDT (id: ${cdtId}) for "${cannGroupName}" — new order starts: ${preview}${suffix}`
              : `reorder attributes in user-modified CDT for "${cannGroupName}" — new order starts: ${preview}${suffix}`;
          },
          parameters: {
            type: 'object',
            properties: {
              cdtId: {
                type: 'string',
                description:
                  'The _id of the user-modified CDT to reorder. ' +
                  'Required when more than one user-modified CDT exists for the current cann group. ' +
                  'Obtain available IDs from the supplementary context for this tool.',
              },
              attributeOrder: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Attribute names in the desired order, top to bottom. ' +
                  'May be a full list or a partial list. Unmentioned attributes retain their ' +
                  'current relative order and are appended after the specified ones.',
              },
            },
            required: ['attributeOrder'],
          },
          handler: async ({ cdtId, attributeOrder } = {}) => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';
            if (!attributeOrder?.length)
              return '[Tool] Action failed: attributeOrder must be a non-empty array of attribute names.';

            const userModifiedCdts = proxy.userModifiedCdts ?? [];
            if (!userModifiedCdts.length)
              return '[Tool] Action failed: no user-modified CDTs found for this cann group.';

            let cdt;
            if (cdtId) {
              cdt = userModifiedCdts.find(c => c._id === cdtId);
              if (!cdt)
                return '[Tool] Action failed: no user-modified CDT with that id found. Check the supplementary context for valid IDs.';
            } else if (userModifiedCdts.length === 1) {
              cdt = userModifiedCdts[0];
            } else {
              const cdtSummary = userModifiedCdts
                .map((c, i) => `#${i + 1}: id=${c._id ?? 'unsaved'}`)
                .join(', ');
              return `[Tool] Action failed: multiple user-modified CDTs exist (${cdtSummary}). Provide the cdtId of the CDT to reorder, or ask the user which one they mean.`;
            }

            const currentAttrs = cdt.attributes ?? [];
            if (!currentAttrs.length)
              return '[Tool] Action failed: this user-modified CDT has no attributes to reorder.';

            // Build a name → attribute entry map (case-insensitive)
            const attrByNameLC = {};
            currentAttrs.forEach(attr => {
              const scenAttr = proxy.scenarioAttributes?.find(s => s.id === attr.attributeId);
              const name = (scenAttr?.name ?? attr.attributeId).toLowerCase();
              attrByNameLC[name] = attr;
            });

            // Validate requested names
            const specifiedLC = attributeOrder.map(n => n.toLowerCase());
            const unknown = specifiedLC.filter(n => !attrByNameLC[n]);
            if (unknown.length) {
              const valid = Object.keys(attrByNameLC).join(', ');
              return `[Tool] Action failed: unknown attribute name(s): ${unknown.join(', ')}. Valid names for this CDT: ${valid}.`;
            }

            // Build new order: specified first (in given order), then remaining in original relative order
            const specifiedSet = new Set(specifiedLC);
            const specifiedAttrs = specifiedLC.map(n => attrByNameLC[n]);
            const remainingAttrs = currentAttrs.filter(attr => {
              const scenAttr = proxy.scenarioAttributes?.find(s => s.id === attr.attributeId);
              const name = (scenAttr?.name ?? attr.attributeId).toLowerCase();
              return !specifiedSet.has(name);
            });
            const reorderedAttrs = [...specifiedAttrs, ...remainingAttrs];

            if (cdt._id) {
              await cdtsStore.updateCdtById({
                cdtId: cdt._id,
                cannGroupId: proxy.selectedCannGroup.key,
                updates: { attributes: reorderedAttrs },
              });
              await cdtsStore.fetchScenarioCdts();
            } else {
              // Unsaved CDT: mutate in-memory store so the child component watcher re-derives local state
              const updated = proxy.scenarioCdts.map(s =>
                s === cdt ? { ...s, attributes: reorderedAttrs } : s
              );
              cdtsStore.setScenarioCdts(updated);
            }

            // Confirm new order from live store
            const updatedCdt = proxy.userModifiedCdts?.find(c =>
              cdt._id ? c._id === cdt._id : !c._id
            );
            const newOrderNames = (updatedCdt?.attributes ?? reorderedAttrs)
              .filter(a => a.isSelected !== false)
              .map(a => {
                const scenAttr = proxy.scenarioAttributes?.find(s => s.id === a.attributeId);
                return scenAttr?.name ?? a.attributeId;
              });

            return `[Tool] Action succeeded: attributes reordered. New order (top to bottom): ${newOrderNames.join(', ')}.`;
          },
        },
        'remove-cdt-attributes': {
          description:
            'Demote one or more attributes from the top (active) section of a user-modified CDT to the lower priority group. ' +
            'This mirrors dragging an attribute from the top section to the lower section in the UI — the attribute is NOT permanently deleted. ' +
            'For saved CDTs the demoted attributes appear in the lower section and can be dragged back to the top. ' +
            'Changes are held in memory and only become permanent when the user clicks Save. ' +
            'Works on both saved and unsaved CDTs — saving is NOT required first. ' +
            'IMPORTANT: the supplementary attribute list below is evaluated at message-send time. ' +
            'If a CDT was just created earlier in this same turn it will not appear in the list below but IS available — call this tool and the handler will find it. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Removing CDT attributes',
          confirmationLabel: args => {
            const cannGroupName = instance.proxy.selectedCannGroup?.name || 'current cann group';
            const names = (args?.attributeNames ?? []).join(', ');
            return `remove attribute(s) [${names}] from user-modified CDT for "${cannGroupName}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              cdtId: {
                type: 'string',
                description:
                  'The _id of the user-modified CDT to modify. ' +
                  'Required when more than one user-modified CDT exists for the current cann group.',
              },
              attributeNames: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Names of the attributes to demote to the lower section (case-insensitive).',
              },
            },
            required: ['attributeNames'],
          },
          handler: async ({ cdtId, attributeNames } = {}) => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';
            if (!attributeNames?.length)
              return '[Tool] Action failed: attributeNames must be a non-empty array of attribute names.';

            const userModifiedCdts = proxy.userModifiedCdts ?? [];
            if (!userModifiedCdts.length)
              return '[Tool] Action failed: no user-modified CDTs found for this cann group.';

            let cdt;
            if (cdtId) {
              cdt = userModifiedCdts.find(c => c._id === cdtId);
              if (!cdt)
                return '[Tool] Action failed: no user-modified CDT with that id found. Check the supplementary context for valid IDs.';
            } else if (userModifiedCdts.length === 1) {
              cdt = userModifiedCdts[0];
            } else {
              const cdtSummary = userModifiedCdts
                .map((c, i) => `#${i + 1}: id=${c._id ?? 'unsaved'}`)
                .join(', ');
              return `[Tool] Action failed: multiple user-modified CDTs exist (${cdtSummary}). Provide the cdtId of the CDT to modify, or ask the user which one they mean.`;
            }

            const currentAttrs = cdt.attributes ?? [];
            if (!currentAttrs.length)
              return '[Tool] Action failed: this user-modified CDT has no attributes.';

            // Build name → attributeId map (case-insensitive), active attributes only
            const activeAttrByNameLC = {};
            currentAttrs.forEach(attr => {
              if (attr.isSelected === false) return;
              const scenAttr = proxy.scenarioAttributes?.find(s => s.id === attr.attributeId);
              const name = (scenAttr?.name ?? attr.attributeId).toLowerCase();
              activeAttrByNameLC[name] = attr.attributeId;
            });

            const requestedLC = attributeNames.map(n => n.toLowerCase());
            const unknown = requestedLC.filter(n => !activeAttrByNameLC[n]);
            if (unknown.length) {
              const valid = Object.keys(activeAttrByNameLC).join(', ');
              return `[Tool] Action failed: unknown or already-demoted attribute name(s): ${unknown.join(', ')}. Active attributes for this CDT: ${valid}.`;
            }

            const demoteIds = new Set(requestedLC.map(n => activeAttrByNameLC[n]));
            const updatedAttrs = currentAttrs.map(attr =>
              demoteIds.has(attr.attributeId) ? { ...attr, isSelected: false } : attr
            );

            if (cdt._id) {
              // Saved CDT: update in-memory store with isSelected:false.
              // getSelectedScenarioAttributes() derives the lower drag-zone from
              // cdt.attributes.filter(a => !a.isSelected), so items appear there immediately.
              // Changes only hit the DB when the user clicks Save.
              const updated = proxy.scenarioCdts.map(s =>
                s === cdt ? { ...s, attributes: updatedAttrs } : s
              );
              cdtsStore.setScenarioCdts(updated);
            } else {
              // Unsaved CDT: getSelectedScenarioAttributes() ignores isSelected on the unsaved CDT
              // and derives the lower section from the tool-generated CDT instead.
              // So we must directly manipulate the child component's local arrays via $ref,
              // exactly as the 'Remove All' button does with removeAllModifiedAttributes().
              const refKey = '_unsaved';
              const cdtComponent = proxy.userModifiedCdtRefs?.[refKey];
              if (cdtComponent) {
                cdtComponent.demoteAttributeIds(demoteIds);
              } else {
                // Fallback: store-level update (attribute won't appear in lower section but leaves top)
                const updated = proxy.scenarioCdts.map(s =>
                  s === cdt ? { ...s, attributes: updatedAttrs } : s
                );
                cdtsStore.setScenarioCdts(updated);
              }
            }

            const remaining = updatedAttrs
              .filter(a => a.isSelected !== false)
              .map(a => {
                const scenAttr = proxy.scenarioAttributes?.find(s => s.id === a.attributeId);
                return scenAttr?.name ?? a.attributeId;
              });

            const savedNote = cdt._id
              ? ' They appear in the lower section and can be dragged back if needed. Save the CDT to make this permanent.'
              : '';
            return `[Tool] Action succeeded: removed [${attributeNames.join(', ')}] from the active attribute list.${savedNote} Remaining active attributes (top to bottom): ${remaining.join(', ')}.`;
          },
        },
        'save-modified-cdt': {
          description:
            'Save the current state of a user-modified CDT for the current cann group. ' +
            'Use this after reordering or removing attributes, or whenever the user asks to save. ' +
            'If more than one user-modified CDT exists, cdtId is required. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Saving modified CDT',
          confirmationLabel: args => {
            const cannGroupName = instance.proxy.selectedCannGroup?.name || 'current cann group';
            const cdtId = args?.cdtId;
            return cdtId
              ? `save user-modified CDT (id: ${cdtId}) for "${cannGroupName}"`
              : `save user-modified CDT for "${cannGroupName}"`;
          },
          parameters: {
            type: 'object',
            properties: {
              cdtId: {
                type: 'string',
                description:
                  'The _id of the user-modified CDT to save. ' +
                  'Required when more than one user-modified CDT exists for the current cann group.',
              },
            },
            required: [],
          },
          handler: async ({ cdtId } = {}) => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';

            const userModifiedCdts = proxy.userModifiedCdts ?? [];
            if (!userModifiedCdts.length)
              return '[Tool] Action failed: no user-modified CDTs found for this cann group.';

            let cdt;
            if (cdtId) {
              cdt = userModifiedCdts.find(c => c._id === cdtId);
              if (!cdt)
                return '[Tool] Action failed: no user-modified CDT with that id found. Check the supplementary context for valid IDs.';
            } else if (userModifiedCdts.length === 1) {
              cdt = userModifiedCdts[0];
            } else {
              const cdtSummary = userModifiedCdts
                .map((c, i) => `#${i + 1}: id=${c._id ?? 'unsaved'}`)
                .join(', ');
              return `[Tool] Action failed: multiple user-modified CDTs exist (${cdtSummary}). Provide the cdtId of the CDT to save.`;
            }

            const refKey = cdt._id ?? '_unsaved';
            const cdtComponent = proxy.userModifiedCdtRefs?.[refKey];
            if (!cdtComponent)
              return '[Tool] Action failed: CDT component not found. Ask the user to try saving manually.';

            await cdtComponent.saveModifiedCdt(cdt);

            // Verify: unsaved CDT should now have an _id; saved CDT should still exist
            const wasUnsaved = !cdt._id;
            const nowExists = proxy.userModifiedCdts?.length > 0;
            if (wasUnsaved && !nowExists)
              return '[Tool] Action failed: CDT could not be saved. Ask the user to try manually.';

            return '[Tool] Action succeeded: user-modified CDT saved.';
          },
        },
        'select-cdt': {
          description:
            'Mark either the tool-generated or user-modified CDT as the active one for the current cann group. ' +
            'The selected CDT drives all downstream operations: sister product matching, assortment canvas layout, and optimisation. ' +
            'Use when the user asks to switch which CDT is selected, or to select one when none is currently active. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Selecting CDT',
          confirmationLabel: args =>
            `set the ${args.cdtType} CDT as the active one for "${instance.proxy.selectedCannGroup?.name || 'current cann group'}"`,
          parameters: {
            type: 'object',
            properties: {
              cdtType: {
                type: 'string',
                enum: ['tool-generated', 'user-modified'],
                description: 'Which CDT to select as the active one.',
              },
            },
            required: ['cdtType'],
          },
          handler: async ({ cdtType }) => {
            const proxy = instance.proxy;
            if (proxy.isEditingDisabled)
              return '[Tool] Action failed: you do not have permission to edit CDTs. Inform the user.';
            if (!proxy.hasCtdGenerated)
              return '[Tool] Action failed: no CDTs have been generated yet. Generate a CDT first.';
            const targetCdt =
              cdtType === 'tool-generated'
                ? proxy.toolGeneratedCdts?.[0]
                : proxy.userModifiedCdts?.[0];
            if (!targetCdt)
              return `[Tool] Action failed: no ${cdtType} CDT found for this cann group.`;
            if (!targetCdt._id)
              return `[Tool] Action failed: the ${cdtType} CDT has not been saved yet. The user must save it first.`;
            if (targetCdt.selected)
              return `[Tool] Action skipped: the ${cdtType} CDT is already selected.`;
            await cdtsStore.updateCdtById({
              cdtId: targetCdt._id,
              updates: { selected: true },
              cannGroupId: proxy.selectedCannGroup.key,
            });
            await cdtsStore.fetchScenarioCdts();
            const updatedCdt =
              cdtType === 'tool-generated'
                ? proxy.toolGeneratedCdts?.[0]
                : proxy.userModifiedCdts?.[0];
            if (!updatedCdt?.selected)
              return `[Tool] Action failed: the ${cdtType} CDT could not be selected. Ask the user to try manually.`;
            return `[Tool] Action succeeded: the ${cdtType} CDT is now selected as the active CDT for downstream analysis.`;
          },
        },
      },
    });

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      beforeNavWithUnsavedData,
    } = useUnsavedDataWarning();
    const { tkey } = useInternationalization('customerDecisionTree');
    return {
      canEditCDTPage,
      aiPanelEnabled,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      tkey,
      beforeNavWithUnsavedData,
    };
  },
  data() {
    return {
      showTreeViewModal: false,
      selectedCannGroup: null,
      // used on cann-group-modal component to update the internal state of the currently selected tab
      // usually matches the `selectedCannGroup.key` object, but in case user has unsaved data and decided to stay
      // this value is reset back to current tab
      dirtySelectedCannGroup: null,
      customAttributes: null,
      treeViewCdt: {},
      hasTooldataChanges: false,
      hasUserChanges: false,
      hasAttributeSelectionChanges: false,
      isRegenerateCDTModalOpen: false,
      generateCannGroupParams: null,
      // Refs to CdtAttributesList instances for user-modified CDTs, keyed by cdt._id or '_unsaved'.
      // Used by the AI remove-cdt-attributes tool to call demoteAttributeIds() directly on the component
      // for unsaved CDTs where store-level isSelected has no effect on the derived lower section.
      userModifiedCdtRefs: {},
    };
  },

  computed: {
    ...mapState(useScenarioCdtsStore, [
      'scenarioCdts',
      'cdtSelectedAttributes',
      'getCdtSelectedAttributesForCannGroup',
    ]),
    ...mapState(useScenariosStore, ['selectedScenario', 'jobStatuses', 'isJobRunning']),
    ...mapState(useClusteringStore, ['getScenarioAttributes']),
    ...mapState(useContextStore, ['showNotImplemented']),
    ...mapState(useAiStore, ['isSidePanelOpen']),

    isEditingDisabled() {
      return !this.canEditCDTPage;
    },

    hasDataChanges() {
      // refs won't work here since the components we want to check hasDataChanges for are inside a v-if
      // emit the value each time it changes and track here instead.
      return some([
        this.hasUnsavedCdts,
        this.hasTooldataChanges,
        this.hasUserChanges,
        this.hasAttributeSelectionChanges,
      ]);
    },

    hasDataErrors() {
      // required for page-actions component
      return false;
    },

    hasUnsavedCdts() {
      // unsaved cdts have no _id
      return this.scenarioCdts.some(v => isUndefined(v._id));
    },

    disableCreateModifiedCdt() {
      if (this.isEditingDisabled) return true;

      // user cannot create a second modified CDT (mCDT) if they are currently working on a non-saved version of the mCDT.
      const hasNonSavedMCDTInfo =
        this.userModifiedCdts.filter(
          cdt => !has(cdt, '_id') && cdt.type === scenarioCdtTypes.userModified
        ).length > 0;
      return hasNonSavedMCDTInfo || this.hasToolGeneratedChanges;
    },

    hasCtdGenerated() {
      if (!this.selectedCannGroup) {
        return false;
      }
      const matchingCdts = this.scenarioCdts.filter(
        ctd => ctd.cannGroupId === this.selectedCannGroup.key
      );
      const hasGeneratedCdts = matchingCdts.filter(el => el.cdt && el.cdt.length > 0).length > 0;
      return hasGeneratedCdts;
    },

    isCdtGenerationJobRunning() {
      return this.isJobRunning('cdtGeneration', get(this.selectedCannGroup, 'key'));
    },

    toolGeneratedCdts() {
      if (!this.scenarioCdts || !this.selectedScenario || !this.selectedCannGroup) {
        return [];
      }
      return this.scenarioCdts.filter(
        s =>
          s.scenarioId === this.selectedScenario._id &&
          s.cannGroupId === this.selectedCannGroup.key &&
          s.type === scenarioCdtTypes.toolGenerated
      );
    },

    userModifiedCdts() {
      if (!this.scenarioCdts || !this.selectedScenario || !this.selectedCannGroup) {
        return [];
      }
      return this.scenarioCdts.filter(
        s =>
          s.scenarioId === this.selectedScenario._id &&
          s.cannGroupId === this.selectedCannGroup.key &&
          s.type === scenarioCdtTypes.userModified
      );
    },

    scenarioAttributes() {
      return this.getScenarioAttributes ? cloneDeep(this.getScenarioAttributes) : [];
    },

    hasToolGeneratedChanges() {
      return this.toolGeneratedCdts.some(cdt => {
        // if the CDT is already generated, it will contain extra properties from the FE
        const sanitisedAttributes = cdt.attributes.map(a =>
          pick(a, ['attributeId', 'rank', 'coefficient', 'standardError'])
        );
        return !isEqual(sanitisedAttributes, cdt.originalJobAttributesSelection);
      });
    },

    progressBarInfo() {
      const cannGroupKey = get(this.selectedCannGroup, 'key');
      if (!cannGroupKey) {
        return {};
      }
      return this.jobStatuses.cdtGeneration[cannGroupKey];
    },
  },

  watch: {
    async hasCtdGenerated(newValue, oldValue) {
      // skip if no change or if it has not been generated.
      if (newValue === oldValue || !newValue) return;
      await this.preloadInitialSelections();
    },
  },

  async created() {
    // if there is already running job
    await this.preloadInitialSelections();
    this.resetHasDataChanges();
    // Signal that page data is ready for the agent to act on.
    useAiStore().setContextReady(true);
  },

  mounted() {
    // Set up AI side panel (outer shell only; inner context is built lazily at send-time)
    const aiStore = useAiStore();
    const contextDisplay = [
      this.selectedScenario?.name,
      this.selectedCannGroup?.name || 'No CANN Group Selected',
    ].filter(Boolean);
    aiStore.enableSidePanel({ section: 'customer-decision-tree', contextDisplay });
    aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
  },

  beforeUnmount() {
    // (no AI panel teardown needed — route watcher in AiSidePanel handles context reset)
  },

  methods: {
    ...mapActions(useScenarioCdtsStore, [
      'runCdtGenerationJob',
      'fetchScenarioCdts',
      'addCdt',
      'updateCdtById',
      'setCdtSelectedAttributes',
      'addUserModifiedCdt',
      'setScenarioCdts',
    ]),
    ...mapActions(useScenariosStore, ['loadScenario', 'updateScenario']),

    resetHasDataChanges() {
      this.hasTooldataChanges = false;
      this.hasUserChanges = false;
      this.hasAttributeSelectionChanges = false;
    },

    registerUserModifiedCdtRef(cdt, el) {
      const key = cdt._id ?? '_unsaved';
      if (el) this.userModifiedCdtRefs[key] = el;
      else delete this.userModifiedCdtRefs[key];
    },

    openTreeViewModal(cdt) {
      this.treeViewCdt = cdt;
      this.showTreeViewModal = true;
    },

    // Update AI panel context when data changes
    buildInnerContext() {
      const buildCdtInfo = (cdt, type, { showAllAttributes = false } = {}) => {
        if (!cdt || !cdt.attributes || cdt.attributes.length === 0) return null;

        let includedAttributes = cdt.attributes.filter(attr => attr.isSelected !== false);
        if (type === 'tool') {
          includedAttributes = includedAttributes
            .filter(attr => attr.rank)
            .sort((a, b) => a.rank - b.rank);
        }
        if (includedAttributes.length === 0)
          return {
            id: cdt._id || null,
            selected: cdt.selected || false,
            includedAttributeCount: 0,
            attributes: [],
          };

        const slice = showAllAttributes ? includedAttributes : includedAttributes.slice(0, 5);
        const attributes = slice.map((attr, index) => {
          const attrObj = this.scenarioAttributes.find(a => a.id === attr.attributeId);
          const name = attrObj?.name || attr.attributeId;
          if (type === 'tool') {
            return {
              rank: attr.rank,
              name,
              coefficient: attr.coefficient ?? null,
              standardError: attr.standardError ?? null,
            };
          }
          return { rank: index + 1, name };
        });

        return {
          id: cdt._id || null,
          selected: cdt.selected || false,
          includedAttributeCount: includedAttributes.length,
          attributes,
        };
      };

      const toolGeneratedSelected = this.toolGeneratedCdts?.[0]?.selected || false;
      const userModifiedSelected = this.userModifiedCdts?.[0]?.selected || false;
      let selectedCdtType = 'none';
      if (toolGeneratedSelected) selectedCdtType = 'tool-generated';
      else if (userModifiedSelected) selectedCdtType = 'user-modified';

      const leafNodes = cannGroupUtils.getLeafNodes(this.selectedScenario?.cannGroups || []);
      const totalCannGroupsCount = leafNodes.length;
      const cannGroupsWithCdtCount = new Set(
        this.scenarioCdts.filter(cdt => cdt.cdt && cdt.cdt.length > 0).map(cdt => cdt.cannGroupId)
      ).size;

      return {
        workpackageId: this.selectedScenario?.workpackageId,
        workpackageName: this.selectedScenario?.workpackageName,
        scenarioId: this.selectedScenario?._id,
        scenarioName: this.selectedScenario?.name,
        cannGroupId: this.selectedCannGroup?.key,
        cannGroupName: this.selectedCannGroup?.name,
        currentView: this.hasCtdGenerated ? 'generated' : 'initial-setup',
        isCdtGenerationJobRunning: this.isCdtGenerationJobRunning,
        isEditingDisabled: this.isEditingDisabled,
        totalCannGroupsCount,
        cannGroupsWithCdtCount,
        hasUnsavedModifiedCdt: this.userModifiedCdts?.some(c => !c._id) || false,
        toolGeneratedCdt: buildCdtInfo(this.toolGeneratedCdts?.[0], 'tool'),
        userModifiedCdts: (this.userModifiedCdts ?? []).map(cdt =>
          buildCdtInfo(cdt, 'user', { showAllAttributes: true })
        ),
        selectedCdtType,
      };
    },

    async preloadInitialSelections() {
      await this.fetchScenarioCdts();
      if (!this.selectedScenario.cannGroups) return;
      const cannGroups = cannGroupUtils.getLeafNodes(this.selectedScenario.cannGroups);

      cannGroups.forEach(cg => {
        const { key } = cg;
        const scenarioAttributes = this.scenarioAttributes;

        const document = find(this.scenarioCdts, item => item.cannGroupId === key);
        // check if attribute selections for this cann group have previously been saved in the database
        if (document && document.attributes && scenarioAttributes.length) {
          const { attributes } = document;
          const attributesMap = keyBy(attributes, 'attributeId');
          // select the previously saved attributes as true
          const selections = scenarioAttributes.map(item => {
            item.selected = !!attributesMap[item.id];
            return item;
          });
          this.setCdtSelectedAttributes({ key, value: selections });
        } else if (scenarioAttributes.length) {
          this.setCdtSelectedAttributes({ key, value: null });
        }
      });
    },

    openRegenerateCdtModal(params) {
      this.generateCannGroupParams = params;
      this.isRegenerateCDTModalOpen = true;
    },

    closeRegenerateCdtModal() {
      this.generateCannGroupParams = null;
      this.isRegenerateCDTModalOpen = false;
    },

    selectCannGroup(cg) {
      this.oldKey = this.dirtySelectedCannGroup;
      this.dirtySelectedCannGroup = cg.key;
      // page is re-constructing
      if (!this.$refs.unsavedDataModal) {
        this.selectedCannGroup = cg;
        return;
      }
      this.beforeNavWithUnsavedData(() => {
        this.selectedCannGroup = cg;
        this.onUnsavedDataModaConfirm();
      });
    },

    onUnsavedDataModalCancel() {
      this.closeUnsavedDataModal();
      this.dirtySelectedCannGroup = this.oldKey;
    },

    onUnsavedDataModaConfirm() {
      // reset any CDT data in page
      this.resetHasDataChanges();
      this.dirtySelectedCannGroup = this.selectedCannGroup.key;
      this.setScenarioCdts(this.scenarioCdts.filter(v => !isUndefined(v._id)));
    },

    async createMissingCdtDocuments(cannGroupIds) {
      const cannGroupsIdsWithoutCdt = difference(
        cannGroupIds,
        map(this.scenarioCdts, property('cannGroupId'))
      );
      const attributesList = map(this.scenarioAttributes, property('id'));

      return Promise.all(
        cannGroupsIdsWithoutCdt.map(cannGroupId => {
          const payload = {
            scenarioId: get(this.selectedScenario, '_id'),
            attributesList,
            cannGroupId,
          };

          return this.addCdt(payload);
        })
      );
    },

    async generateCdt(cannGroupIds) {
      await this.createMissingCdtDocuments(cannGroupIds);
      const payload = { cannGroupIds };
      await this.runCdtGenerationJob(payload);
      await this.loadScenario(this.selectedScenario._id);
    },

    // wrapper for initial generation since no popup is shown
    async startInitialCdtGeneration(params) {
      this.generateCannGroupParams = params;
      await this.startCdtGeneration();
    },

    async startCdtGeneration() {
      const { cannGroupIds, updatePayload } = this.generateCannGroupParams;
      this.closeRegenerateCdtModal();
      if (updatePayload) await this.updateCdtById(updatePayload);
      await this.generateCdt(cannGroupIds);
    },

    createModifiedCDT() {
      // need to use cloneDeep here, because otherwise we'll be making changes to the original one,
      // since toolGeneratedCdt will have its reference otherwise.
      const toolGeneratedCdt = cloneDeep(this.toolGeneratedCdts[0]);
      toolGeneratedCdt.sourceId = toolGeneratedCdt._id;
      delete toolGeneratedCdt._id;
      const attributes = toolGeneratedCdt.attributes.filter(a => a.coefficient && a.rank);
      const newObj = {
        ...toolGeneratedCdt,
        ...{ attributes },
        ...{ type: scenarioCdtTypes.userModified, selected: false },
      };
      this.addUserModifiedCdt(newObj);
    },

    closeCdtTreeViewModal() {
      this.showTreeViewModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.flex-cdt-container {
  height: 100%;

  .inner-flex-container {
    overflow: hidden;
    height: $tab-panel-height;
  }
}

:deep(.dialog-card__header) {
  padding: 0px 15px 0px 15px;
}

:deep(.dialog-card__footer) {
  display: none !important;
}

:deep(.dialog-card__content) {
  height: 100%;
  max-height: 100% !important;
  padding: 0px 20px 20px 20px;
}

.cdt-container {
  height: 100%;
  max-width: none;
  position: relative;

  .heading-row {
    height: 48px;
    min-height: 48px;
    max-height: 48px;
  }

  .page-actions-container {
    border-top: 1px solid $assortment-divider-colour;

    .generate-multiple-btn-container {
      display: inline-block;
      padding: 0 8px;
      border-right: 1px solid $assortment-horizontal-border-colour;
    }
  }

  .cdt-panel {
    height: $tab-panel-height;

    > .v-col {
      max-height: 100%;
      overflow: hidden;
    }
  }

  .progress-bar {
    padding: 0;
    margin: 10% auto auto auto;
  }

  .addModifiedCdt {
    color: $assortment-action-icon-color;
  }

  .attributes-actions-container {
    border-top: 1px solid $assortment-divider-colour;
    min-height: 52px;
  }
}
</style>
